import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  acquisitionsApi,
  type Vendor,
  type PurchaseOrder,
  type PurchaseOrderDetail,
  type POLine,
} from "@/lib/api/acquisitions";
import { Loader2, Save, Plus, Trash2, Info } from "lucide-react";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";

interface InvoiceLine {
  /** When set, match applies encumbrance → expenditure for this fund on the PO line */
  poLineId?: string;
  description: string;
  quantity: string;
  unitPrice: string;
}

function lineFromPoLine(pl: POLine): InvoiceLine {
  return {
    poLineId: pl.id,
    description: pl.title,
    quantity: String(pl.quantity),
    unitPrice: String(pl.unitPrice),
  };
}

export default function NewAcqInvoicePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [poList, setPoList] = useState<PurchaseOrder[]>([]);
  const [loadingPoList, setLoadingPoList] = useState(false);
  const [linkedPo, setLinkedPo] = useState<PurchaseOrderDetail | null>(null);
  const [loadingPoDetail, setLoadingPoDetail] = useState(false);

  const [form, setForm] = useState({
    vendorId: searchParams.get("vendorId") || "",
    purchaseOrderId: searchParams.get("poId") || "",
    invoiceNumber: "",
    invoiceDate: new Date().toISOString().slice(0, 10),
    dueDate: "",
    currency: "NGN",
    notes: "",
  });

  const [lines, setLines] = useState<InvoiceLine[]>([
    { description: "", quantity: "1", unitPrice: "" },
  ]);

  useEffect(() => {
    acquisitionsApi.vendors
      .list({ isActive: true, limit: 200 })
      .then((res) => setVendors(res.vendors))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.vendorId) {
      setPoList([]);
      return;
    }
    let cancelled = false;
    setLoadingPoList(true);
    acquisitionsApi.orders
      .list({ vendorId: form.vendorId, limit: 100 })
      .then((res) => {
        if (!cancelled) setPoList(res.orders);
      })
      .catch(() => {
        if (!cancelled) setPoList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingPoList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.vendorId]);

  useEffect(() => {
    if (!form.purchaseOrderId || !form.vendorId) {
      setLinkedPo(null);
      return;
    }
    let cancelled = false;
    setLoadingPoDetail(true);
    acquisitionsApi.orders
      .getById(form.purchaseOrderId)
      .then((po) => {
        if (cancelled) return;
        if (po.vendorId !== form.vendorId) {
          toast.error(
            "This purchase order belongs to another vendor. Pick a PO for the selected vendor."
          );
          setForm((prev) => ({ ...prev, purchaseOrderId: "" }));
          setLinkedPo(null);
          return;
        }
        setLinkedPo(po);
      })
      .catch(() => {
        if (!cancelled) {
          setLinkedPo(null);
          toast.error("Could not load purchase order.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPoDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.purchaseOrderId, form.vendorId]);

  const totalAmount = lines.reduce(
    (s, l) => s + (parseFloat(l.unitPrice) || 0) * (parseInt(l.quantity, 10) || 0),
    0
  );

  const linesMissingPoLink =
    linkedPo &&
    lines.some((l) => !l.poLineId && (l.description.trim() || parseFloat(l.unitPrice) > 0));

  const handleSubmit = async () => {
    if (!form.vendorId || !form.invoiceNumber.trim() || lines.length === 0) {
      toast.error("Vendor, invoice number, and at least one line are required");
      return;
    }

    setSaving(true);
    try {
      await acquisitionsApi.acqInvoices.create({
        vendorId: form.vendorId,
        purchaseOrderId: form.purchaseOrderId || undefined,
        invoiceNumber: form.invoiceNumber.trim(),
        invoiceDate: form.invoiceDate,
        dueDate: form.dueDate || undefined,
        totalAmount,
        currency: form.currency,
        notes: form.notes || undefined,
        lines: lines.map((l) => ({
          poLineId: l.poLineId || undefined,
          description: l.description || undefined,
          quantity: parseInt(l.quantity, 10) || 1,
          unitPrice: parseFloat(l.unitPrice) || 0,
        })),
      });
      toast.success("Invoice created.");
      navigate("/acquisitions/invoices");
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, "Failed to create invoice."));
    } finally {
      setSaving(false);
    }
  };

  const update = (field: string, value: string) => setForm((prev) => ({ ...prev, [field]: value }));

  const setVendorId = (vendorId: string) => {
    setForm((prev) => ({ ...prev, vendorId, purchaseOrderId: "" }));
    setLinkedPo(null);
  };

  const updateLine = (idx: number, field: keyof InvoiceLine, value: string) => {
    const updated = [...lines];
    updated[idx] = { ...updated[idx], [field]: value };
    setLines(updated);
  };

  const setLinePoLine = (idx: number, poLineId: string | undefined, poLines: POLine[]) => {
    setLines((prev) => {
      const updated = [...prev];
      if (!poLineId) {
        updated[idx] = { ...updated[idx], poLineId: undefined };
        return updated;
      }
      const pl = poLines.find((p) => p.id === poLineId);
      if (!pl) return prev;
      updated[idx] = lineFromPoLine(pl);
      return updated;
    });
  };

  const addLine = () =>
    setLines((prev) => [...prev, { description: "", quantity: "1", unitPrice: "" }]);

  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const fillLinesFromPo = () => {
    if (!linkedPo?.lines.length) return;
    setLines(linkedPo.lines.map((pl) => lineFromPoLine(pl)));
  };

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN" }).format(n);

  return (
    <PageContainer width="wide">
      <PageHeader
        title="New Vendor Invoice"
        description="Record a vendor bill and link it to a PO so matching updates encumbrances and PO status."
        breadcrumbItems={[
          { label: "Invoices", href: "/acquisitions/invoices" },
          { label: "New Vendor Invoice" },
        ]}
        showSearch={false}
        titleSize="md"
      />

      <Alert variant="info" className="items-start">
        <Info className="h-4 w-4" />
        <AlertDescription>
          <span className="font-medium">How matching works</span>
          <span className="block mt-1 text-slate-700">
            After you save, open the invoice and click <strong>Match Invoice</strong>. If a{" "}
            <strong>purchase order</strong> is linked, the PO moves to <strong>Invoiced</strong>. If
            each line is linked to a <strong>PO line</strong> that has a fund, the system releases
            encumbrance and records expenditure (PO must usually be <strong>sent</strong> first so
            encumbrances exist).
          </span>
        </AlertDescription>
      </Alert>

      <div className="rounded-lg border bg-white p-6 space-y-4">
        <h3 className="font-semibold text-slate-900">Invoice Details</h3>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label required>Vendor</Label>
            <Select value={form.vendorId} onValueChange={setVendorId}>
              <SelectTrigger>
                <SelectValue placeholder="Select vendor" />
              </SelectTrigger>
              <SelectContent>
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name} ({v.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label required>Invoice Number</Label>
            <Input
              value={form.invoiceNumber}
              onChange={(e) => update("invoiceNumber", e.target.value)}
              placeholder="e.g. INV-2026-001"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Purchase order (optional)</Label>
          <Select
            value={form.purchaseOrderId || "__none__"}
            onValueChange={(v) => update("purchaseOrderId", v === "__none__" ? "" : v)}
            disabled={!form.vendorId || loadingPoList}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  !form.vendorId
                    ? "Select a vendor first"
                    : loadingPoList
                      ? "Loading…"
                      : "No PO linked"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">No purchase order</SelectItem>
              {poList.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.orderNumber} — {o.status}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {loadingPoDetail && form.purchaseOrderId && (
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading PO lines…
            </p>
          )}
          {linkedPo && !loadingPoDetail && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button type="button" size="sm" variant="secondary" onClick={fillLinesFromPo}>
                Fill invoice lines from PO
              </Button>
              <span className="text-xs text-slate-500">
                One row per PO line with links set — edit amounts if the vendor invoice differs.
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Invoice Date</Label>
            <Input
              type="date"
              value={form.invoiceDate}
              onChange={(e) => update("invoiceDate", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Due Date</Label>
            <Input
              type="date"
              value={form.dueDate}
              onChange={(e) => update("dueDate", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Currency</Label>
            <Input
              value={form.currency}
              onChange={(e) => update("currency", e.target.value)}
              placeholder="NGN"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Notes</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={2}
            placeholder="Optional internal notes"
          />
        </div>
      </div>

      <div className="rounded-lg border bg-white p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h3 className="font-semibold text-slate-900">Invoice Lines</h3>
          <Button size="sm" variant="outline" onClick={addLine}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add Line
          </Button>
        </div>

        {linkedPo && linesMissingPoLink && (
          <Alert variant="warning" className="items-start">
            <Info className="h-4 w-4" />
            <AlertDescription>
              Some lines are not linked to a PO line. Those lines will not move fund encumbrance /
              expenditure on match — only the PO status update (if a PO is linked) still applies.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {lines.map((line, idx) => (
            <div
              key={idx}
              className="flex flex-col gap-3 p-3 rounded-lg border border-slate-100 bg-slate-50/50 sm:flex-row sm:flex-wrap sm:items-end"
            >
              {linkedPo && linkedPo.lines.length > 0 && (
                <div className="w-full sm:w-[min(100%,280px)] space-y-1">
                  <Label className="text-xs">PO line (for budget match)</Label>
                  <Select
                    value={line.poLineId || "__none__"}
                    onValueChange={(v) =>
                      setLinePoLine(idx, v === "__none__" ? undefined : v, linkedPo.lines)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Not linked" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not linked</SelectItem>
                      {linkedPo.lines.map((pl) => (
                        <SelectItem key={pl.id} value={pl.id}>
                          {pl.title.length > 48 ? `${pl.title.slice(0, 48)}…` : pl.title} —{" "}
                          {pl.quantity} × {Number(pl.unitPrice).toLocaleString()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex-1 min-w-[120px] space-y-1">
                <Label className="text-xs">Description</Label>
                <Input
                  value={line.description}
                  onChange={(e) => updateLine(idx, "description", e.target.value)}
                  placeholder="Line item description"
                />
              </div>
              <div className="w-20 space-y-1">
                <Label className="text-xs">Qty</Label>
                <Input
                  type="number"
                  min="1"
                  value={line.quantity}
                  onChange={(e) => updateLine(idx, "quantity", e.target.value)}
                  placeholder="1"
                />
              </div>
              <div className="w-28 space-y-1">
                <Label className="text-xs">Unit Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={line.unitPrice}
                  onChange={(e) => updateLine(idx, "unitPrice", e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <Button
                size="icon"
                variant="ghost"
                className="text-slate-400 hover:text-red-500 shrink-0"
                onClick={() => removeLine(idx)}
                disabled={lines.length === 1}
                type="button"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex justify-end pt-2 border-t">
          <span className="text-sm text-slate-500">Total:</span>
          <span className="ml-2 font-bold tabular-nums">{formatCurrency(totalAmount)}</span>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate("/acquisitions/invoices")}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          <Save className="mr-2 h-4 w-4" />
          Save Invoice
        </Button>
      </div>
    </PageContainer>
  );
}
